import { useTranslation } from 'react-i18next';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import { alpha, Box, CardActions, CircularProgress, IconButton, styled, Tooltip } from '@mui/material';
import { Link } from 'react-router';
import ProductCatalogCardBadges from './ProductCatalogCardBadges';
import ProductCatalogCardContent from './ProductCatalogCardContent';
import ProductCatalogCardImage from './ProductCatalogCardImage';
import ErrorBoundary from '../../Common/_components/ErrorBoundary';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import { memo, type MouseEvent, useCallback, useMemo, useState } from 'react';
import CardButton from '../../Common/Buttons/CardButton';
import { useRecentlyViewedProducts } from '../../Product/recentlyViewedProducts';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch';
import { SaveFavoriteViewProduct, SetFavoriteViewProduct } from '../../UserData/_actions/UserDataActions';
import useIsSaleProduct, { SALE_DISCOUNT_PERCENT } from '../../Common/_hooks/useIsSaleProduct';
import buildCreateUrl from '../buildCreateUrl';

export type ProductCatalogCardVariant = 'catalog' | 'selector';

export interface Props {
  product: ViewProductStore
  hoverColor: string
  createUrlSearchParams?: URLSearchParams
  onSelect?: (product: ViewProductStore) => void
  onDoubleClick?: (product: ViewProductStore) => void
  selectOnly?: boolean
  replaceCreateUrl?: boolean
  selected?: boolean
  variant?: ProductCatalogCardVariant
}

interface CardBorderProps {
  hoverColor: string
  selected: boolean
}

interface FavoriteButtonProps {
  favorited: boolean
}

const CardBorder = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hoverColor' && prop !== 'selected',
})<CardBorderProps>(({ hoverColor, selected, theme }) => ({
  boxSizing: 'border-box',
  background: '#FFFFFF',
  border: '1px solid ' + (selected ? 'transparent' : 'rgba(0, 0, 0, 0.08)'),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  position: 'relative',
  transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
  borderRadius: '10px',
  overflow: 'visible',
  '--product-catalog-card-image-background': '#e5e5e5',
  '--product-catalog-card-content-color': '#333333',
  '--product-catalog-card-description-color': '#000',
  '--product-catalog-card-print-color': theme.palette.accent.main,

  '&:hover': {
    borderColor: selected ? 'transparent' : alpha(hoverColor, 0.5),
    boxShadow: '0 10px 28px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
    //'--product-catalog-card-image-background': alpha(hoverColor, 0.14),

    '.product-catalog-card-primary-image:not(.has-hover-image) img': {
      opacity: .8,
    },

    '.product-catalog-card-hover-image': {
      opacity: 1,
    },
  },

  '&::after': {
    border: '2px solid #000',
    borderRadius: 'inherit',
    bottom: -1,
    boxSizing: 'border-box',
    content: '""',
    left: -1,
    opacity: selected ? 1 : 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: -1,
    top: -1,
    zIndex: 5,
    transition: 'opacity 180ms ease',
  },

  'a:hover': {
    textDecoration: 'none',
  }
}));

const ProductImageLink = styled(Link)({
  display: 'block',
  position: 'relative',
});

const ImageContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  paddingBottom: '100%',
  backgroundColor: 'var(--product-catalog-card-image-background)',
  borderRadius: '9px 9px 0 0',
  overflow: 'hidden',
  transition: 'background-color 350ms',

  'img': {
    width: '100%',
    maxWidth: '100%',
    position: 'absolute',
    top: '0',
    left: '0',
    transition: 'opacity 250ms',
  },

  '.product-catalog-card-hover-image': {
    // Fade over the opaque primary image so the background never flashes through.
    opacity: 0,
    transition: 'opacity 250ms',
  },
});

const SaleRibbon = styled('div')(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.accent.main,
  bottom: 20,
  color: theme.palette.accent.contrastText,
  display: 'flex',
  fontFamily: '"Bebas Neue", sans-serif',
  fontSize: 24,
  fontWeight: 400,
  height: 40,
  letterSpacing: 0,
  lineHeight: 1,
  padding: '0 12px',
  position: 'absolute',
  right: 0,
  textTransform: 'uppercase',
  zIndex: 2,

  '&:after': {
    borderBottom: '20px solid',
    borderBottomColor: theme.palette.accent.main,
    borderLeft: '16px solid transparent',
    borderRight: '1px solid',
    borderRightColor: theme.palette.accent.main,
    borderTop: '20px solid',
    borderTopColor: theme.palette.accent.main,
    content: '""',
    left: -16,
    position: 'absolute',
    top: 0,
  },

  [theme.breakpoints.down('sm')]: {
    bottom: 14,
    fontSize: 20,
    height: 34,
    padding: '0 10px',

    '&:after': {
      borderBottomWidth: 17,
      borderLeftWidth: 14,
      borderTopWidth: 17,
      left: -14,
    },
  },
}));

const FavoriteButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'favorited',
})<FavoriteButtonProps>(({ favorited, theme }) => ({
  //backgroundColor: alpha(theme.palette.common.white, 0.94),
  //border: '1px solid',
  //borderColor: favorited ? alpha(theme.palette.accent.main, 0.34) : alpha(theme.palette.common.black, 0.1),
  //boxShadow: '0 6px 14px rgba(0, 0, 0, 0.08)',
  color: favorited ? theme.palette.accent.main : alpha(theme.palette.text.primary, 0.7),
  height: 36,
  transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease',
  width: 36,

  '&:hover': {
    //backgroundColor: theme.palette.common.white,
    //borderColor: alpha(theme.palette.accent.main, 0.42),
    color: theme.palette.accent.main,
    transform: 'scale(1.04)',
  },

  '&.Mui-disabled': {
    //backgroundColor: alpha(theme.palette.common.white, 0.86),
    color: favorited ? alpha(theme.palette.accent.main, 0.5) : alpha(theme.palette.text.primary, 0.32),
  },

  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
}));

const FavoriteButtonAnchor = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  left: theme.spacing(0.25),
  position: 'absolute',
  top: theme.spacing(0.25),
  zIndex: 10,
}));

const ProductActions = styled(CardActions)({
  background: '#fff',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  marginTop: 'auto',
  padding: 0,
  borderRadius: '0 0 10px 10px',
  overflow: 'hidden',

  '& > :not(:first-of-type)': {
    marginLeft: 0,
  },
});

const ProductActionButton = styled(CardButton)(({ theme }) => ({
  borderRadius: 0,
  fontWeight: 700,
  justifyContent: 'center',
  minHeight: '48px',
  minWidth: 0,
  padding: theme.spacing(1.25, 1),
  textTransform: 'none',
  width: '100%',
}));

const ProductInfoButton = styled(ProductActionButton)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRight: '1px solid rgba(0, 0, 0, 0.08)',
  color: theme.palette.text.primary,

  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.04),
  },
}));

const CreateButton = styled(ProductActionButton)(({ theme }) => ({
  color: theme.palette.accent.main,
  //color: '#fff',

  '&:hover': {
    backgroundColor: alpha(theme.palette.accent.main, 0.1),
  },
}));


function ProductCatalogCard(props: Props) {
  const [t] = useTranslation('products');
  const [tR] = useTranslation('routes');
  const variant = props.variant ?? 'catalog';
  const isSelectorCard = variant === 'selector';
  const dispatch = useAppDispatch();
  const createProduct = useAppSelector(state => (
    props.product.get('id_product') !== null
      ? state.get('appData').get('products').get(String(props.product.get('id_product')))
      : undefined
  ));
  const isFavorite = useAppSelector(state => state.get('userData').get('favoriteViewProducts').includes(props.product.get('id')));
  const isSaleProduct = useIsSaleProduct();
  const { addRecentlyViewedProduct } = useRecentlyViewedProducts();
  const [favoriteSaving, setFavoriteSaving] = useState(false);
  const isOnSale = isSaleProduct(props.product.get('id_product'));

  const productInfoUrl = tR('/products') + '/' + props.product.get('url_slug');
  const createUrl = useMemo(() => {
    return buildCreateUrl(props.product, createProduct, tR('/create'), props.createUrlSearchParams);
  }, [createProduct, props.createUrlSearchParams, props.product, tR]);
  const onProductInfoClick = useCallback(() => {
    addRecentlyViewedProduct(props.product.get('id'));
  }, [addRecentlyViewedProduct, props.product])
  const onSelectorClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    if (props.selectOnly === true) {
      event.preventDefault();
    }

    props.onSelect?.(props.product);
  }, [props.onSelect, props.product, props.selectOnly])
  const onSelectorDoubleClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    if (props.selectOnly === true) {
      event.preventDefault();
    }

    props.onDoubleClick?.(props.product);
  }, [props.onDoubleClick, props.product, props.selectOnly])
  const onFavoriteClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (favoriteSaving) return;

    const nextFavorite = !isFavorite;
    setFavoriteSaving(true);
    dispatch(SetFavoriteViewProduct(props.product.get('id'), nextFavorite));
    dispatch(SaveFavoriteViewProduct(props.product.get('id'), nextFavorite).set({
      onSuccess: () => setFavoriteSaving(false),
      onFailure: () => setFavoriteSaving(false),
      onAbort: () => setFavoriteSaving(false),
    }));
  }, [dispatch, favoriteSaving, isFavorite, props.product])

  const favoriteButton = <Tooltip title={isFavorite ? t('Remove from favorites') : t('Add to favorites')} placement="top">
    <FavoriteButtonAnchor>
      <FavoriteButton
        aria-label={isFavorite ? t('Remove from favorites') : t('Add to favorites')}
        aria-pressed={isFavorite}
        disabled={favoriteSaving}
        favorited={isFavorite}
        onClick={onFavoriteClick}
        size="small"
      >
        {favoriteSaving
          ? <CircularProgress color="inherit" size={18} thickness={4} />
          : isFavorite ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
      </FavoriteButton>
    </FavoriteButtonAnchor>
  </Tooltip>;

  const cardContent = <>
    <Box
      component="div"
      sx={{
        position: 'relative',
      }}
    >
      {!isSelectorCard ? favoriteButton : null}
      <ProductCatalogCardBadges product={props.product} />
      {isSelectorCard ? <ImageContainer>
        <ErrorBoundary fallback={<></>}>
          <ProductCatalogCardImage
            slug={props.product.get('slug')}
            name={props.product.get('name')}
          />
        </ErrorBoundary>
        {isOnSale ? <SaleRibbon>{t('{{percent}}% OFF', { percent: SALE_DISCOUNT_PERCENT })}</SaleRibbon> : null}
      </ImageContainer> : <ProductImageLink
          to={productInfoUrl}
          reloadDocument={!props.product.get('is_react_page')}
          onClick={onProductInfoClick}
        >
        <ImageContainer>
          <ErrorBoundary fallback={<></>}>
            <ProductCatalogCardImage
              slug={props.product.get('slug')}
              name={props.product.get('name')}
            />
          </ErrorBoundary>
          {isOnSale ? <SaleRibbon>{t('{{percent}}% OFF', { percent: SALE_DISCOUNT_PERCENT })}</SaleRibbon> : null}
        </ImageContainer>
      </ProductImageLink>}
    </Box>
    <ProductCatalogCardContent
      product={props.product}
      productInfoUrl={isSelectorCard ? undefined : productInfoUrl}
      onProductInfoClick={isSelectorCard ? undefined : onProductInfoClick}
      isOnSale={isOnSale}
      showPricing={!isSelectorCard}
    />
    {!isSelectorCard ? <ProductActions>
      <ProductInfoButton
        component={Link}
        to={productInfoUrl}
        reloadDocument={!props.product.get('is_react_page')}
        onClick={onProductInfoClick}
      >{t('Info')}</ProductInfoButton>
      <CreateButton
        href={createUrl}
      >{t('Create')}</CreateButton>
    </ProductActions> : null}
  </>;

  if (isSelectorCard) {
    return <CardBorder hoverColor={props.hoverColor} selected={props.selected === true}>
      {favoriteButton}
      <Box
        component={Link}
        to={createUrl}
        replace={props.replaceCreateUrl}
        onClick={onSelectorClick}
        onDoubleClick={onSelectorDoubleClick}
        sx={{
          color: 'inherit',
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          textDecoration: 'none',
          width: '100%',

          '&:hover': {
            textDecoration: 'none',
          },
        }}
      >
        {cardContent}
      </Box>
    </CardBorder>
  }

  return <CardBorder hoverColor={props.hoverColor} selected={false}>
    {cardContent}
  </CardBorder>
}

export default memo(ProductCatalogCard);
